import get from 'lodash/get';
import { snakeToCamel } from '@eb/string-utils';
import {
    getBuiltSurvey,
    shouldIncludeInBuyerQuestions,
    shouldIncludeInBasicInfo,
    shouldIncludeInAttendeeQuestions,
} from '../models/survey';

import { CannedQuestionName } from '../api/models/survey';
import { getFieldPrefilledValue } from '../api/transformations/survey';
import {
    isTransferredOrder,
    shouldManageUserSavedSurveyData,
} from '../selectors/order';

import { createSelector } from './utils/selector';
import {
    getEmailToPrefill,
    getFirstNameToPrefill,
    getLastNameToPrefill,
} from '../utils/user';

import { LOCALIZED_QUESTIONS_CHOICES_BY_QUESTION_ID } from '../models/survey';
import {
    CANNED_QUESTION_EMAIL_ADDRESS,
    CANNED_QUESTION_FIRST_NAME,
    CANNED_QUESTION_LAST_NAME,
    getFieldName,
    SELECTED_QUESTION_GROUP_BUYER,
} from '../containers/checkout/constants';

import gettext from '@eb/gettext';

export const getQuestionsCollection = (state) =>
    get(state, 'survey.questionsCollection');
export const getInteractiveQuestions = (state) =>
    get(state, 'survey.interactiveQuestions');

export const _shouldGetChoicesText = (choices, questionId) =>
    LOCALIZED_QUESTIONS_CHOICES_BY_QUESTION_ID[questionId]
        ? LOCALIZED_QUESTIONS_CHOICES_BY_QUESTION_ID[questionId]
        : choices;

const _getAttendeeSurveyTitleText = (ticketNumber, ticketClassName) =>
    gettext('Ticket %(ticketNumber)s · %(ticketClassName)s', {
        ticketNumber,
        ticketClassName,
    });

export const getCountries = (state) => get(state, 'address.countries', {});

export const _buildAttendeeSurveys = (
    attendees = [],
    questions,
    shouldIncludeQuestionHelper,
) =>
    attendees.reduce(
        (attendeeSurvey, { id, survey = [], ticketClassName }, index) => {
            if (survey.length > 0) {
                attendeeSurvey.push({
                    id,
                    survey: getBuiltSurvey(
                        questions,
                        survey,
                        shouldIncludeQuestionHelper,
                    ),
                    ticketClassName,
                    ticketNumber: index + 1,
                });
            }

            return attendeeSurvey;
        },
        [],
    );

// Return questionsForBasicInfo
export const getQuestionsForBasicInfo = createSelector(
    (state) => getQuestionsCollection(state),
    (state) => getInteractiveQuestions(state),
    (questionsCollection, interactiveQuestions) =>
        getBuiltSurvey(
            questionsCollection,
            interactiveQuestions,
            shouldIncludeInBasicInfo,
        ),
);

// Return questionsForAttendees object
export const getQuestionsForAttendees = createSelector(
    (state) => get(state, 'order.attendees'),
    (state) => getQuestionsCollection(state),
    (attendees, questionsCollection) =>
        _buildAttendeeSurveys(
            attendees,
            questionsCollection,
            shouldIncludeInAttendeeQuestions,
        ),
);

// Return questionsForBuyer object
export const getQuestionsForBuyer = createSelector(
    (state) => getQuestionsCollection(state),
    (state) => getInteractiveQuestions(state),
    (state) => get(state, 'order.taxIdName'),
    (questions, survey, taxIdName) =>
        getBuiltSurvey(
            questions,
            survey,
            shouldIncludeInBuyerQuestions,
            taxIdName,
        ),
);

export const getSurvey = (state) => ({
    questionsForAttendees: getQuestionsForAttendees(state),
    questionsForBuyer: getQuestionsForBuyer(state),
    questionsForBasicInfo: getQuestionsForBasicInfo(state),
});

export const getAttendeesSelectorOptions = createSelector(
    (state) => get(state, 'order.attendees'),
    getQuestionsForAttendees,
    (attendees, attendeeSurvey) =>
        attendeeSurvey.map(({ id, ticketClassName }) => {
            const ticketIndex = attendees.findIndex(
                ({ id: attendeeId }) => id === attendeeId,
            );

            return {
                value: id,
                display: _getAttendeeSurveyTitleText(
                    ticketIndex + 1,
                    ticketClassName,
                ),
            };
        }),
);

export const getFirstAttendeeWithSurvey = (state) => {
    const {
        order: { attendees = [] },
    } = state;

    return attendees.find(
        (attendee) => attendee.survey && attendee.survey.length,
    );
};

export const getSurveyResponseNameValues = (state) => {
    const {
        // TODO: We sohuld pick the attendee-level questions, too
        survey: { interactiveQuestions = [], surveyResponsesByAttendeeId = {} },
    } = state;

    const existingQuestionsByAttendee = {
        [SELECTED_QUESTION_GROUP_BUYER]: new Set(
            interactiveQuestions.map(({ questionId }) => questionId),
        ),
        // TODO: Include attendee-level questions here
    };
    const questionsCollection = getQuestionsCollection(state);

    return Object.entries(surveyResponsesByAttendeeId)
        .flatMap(([attendeeId, attendeeResponses = {}]) =>
            Object.entries(attendeeResponses).map(
                ([surveyFieldRawName, surveyFieldValue]) => ({
                    attendeeId,
                    surveyFieldRawName,
                    surveyFieldValue,
                }),
            ),
        )
        .filter(({ attendeeId, surveyFieldRawName }) =>
            existingQuestionsByAttendee[attendeeId]?.has(surveyFieldRawName),
        )
        .map(
            (
                { attendeeId, surveyFieldRawName, surveyFieldValue },
                index,
                allResponses,
            ) => {
                // TODO: Remove this hack once both EB-154855 and EB-154854 are fixed
                // This hack is here to convert isBusiness value to proper format. isBusiness is a radio-button canned
                // question which means its value should be a number (the index of the option selected). However, this
                // specific canned question is being stored as a boolean value and it's always `true`.
                // The only way to know which option is the one the attendee chose is checking if `taxRegistrationId`
                // question is present: that question is asked only when the attendee signed up as a business and it's a
                // required question.
                if (surveyFieldRawName === CannedQuestionName.isBusiness) {
                    const isBusiness = allResponses.find(
                        ({ surveyFieldRawName }) =>
                            surveyFieldRawName ===
                            CannedQuestionName.taxRegistrationId,
                    );

                    const id = isBusiness ? '1' : '0';

                    return {
                        attendeeId,
                        surveyFieldRawName,
                        surveyFieldValue: [{ id }],
                    };
                }

                return { attendeeId, surveyFieldRawName, surveyFieldValue };
            },
        )
        .reduce(
            (acc, { attendeeId, surveyFieldRawName, surveyFieldValue }) => ({
                ...acc,
                ...(surveyFieldValue
                    ? {
                          [getFieldName(
                              attendeeId,
                              surveyFieldRawName,
                          )]: getFieldPrefilledValue({
                              value: surveyFieldValue,
                              type:
                                  questionsCollection[
                                      snakeToCamel(surveyFieldRawName)
                                  ].type,
                          }),
                      }
                    : {}),
            }),
            {},
        );
};

export const getPrefillSurveyInfo = (state) => ({
    emailToPrefill: getEmailToPrefill(state),
    firstNameToPrefill: getFirstNameToPrefill(state),
    lastNameToPrefill: getLastNameToPrefill(state),
    attendeeWithSurvey: getFirstAttendeeWithSurvey(state),
    isTransferredOrder: isTransferredOrder(state),
    surveyResponseNameValues: getSurveyResponseNameValues(state),
    shouldManageUserSavedSurveyData: shouldManageUserSavedSurveyData(state),
});
